import { useMemo } from 'react';
import { atom, useAtom, useAtomValue, type WritableAtom } from 'jotai';

import { createMutationAtom } from 'atoms/create-resource-atom';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import type {
  InventoryWorkspaceResponse,
  TypeCurveHeader,
} from 'types/api-responses';
import type {
  TypeCurveWorkspaceResponse,
  TypeCurveWorkspacesListResponse,
} from 'types/type-curve-workspace';
import type { TcWorkspaceHeaderResponse } from 'types/type-curve-workspace-show';

const createTypeCurveWorkspaceAtom = createMutationAtom<
  TypeCurveWorkspaceResponse,
  'createTypeCurveWorkspace'
>();
const promoteTypeCurveWorkspaceAtom = createMutationAtom<
  TypeCurveWorkspaceResponse,
  'promoteTypeCurveWorkspace'
>();
const createInventoryWorkspaceAtom = createMutationAtom<
  InventoryWorkspaceResponse,
  'createInventoryWorkspace'
>();

/** Active Type Curve verification workspace for the Type Curve Details page (null = idle header). */
const activeTcWorkspaceAtom = atom<TypeCurveWorkspaceResponse | null>(null);

/** ID of the most recently promoted workspace (from the header response). */
const latestPromotedWorkspaceIdAtom = atom<number | null>(null);

/**
 * Effective workspace ID for read-only queries (map, slot table, gun barrel, unit table).
 * Returns the active workspace ID when inside a workspace, otherwise falls back
 * to the latest promoted workspace ID so queries reflect the promoted state.
 */
const tcEffectiveWorkspaceIdAtom = atom<number | null>(
  (get) => get(activeTcWorkspaceAtom)?.id ?? get(latestPromotedWorkspaceIdAtom)
);

/**
 * Whether the workspace `tcEffectiveWorkspaceIdAtom` points at is promoted, which makes it
 * read-only. Inside a workspace that is the workspace's own flag. On the Type Curve Details page
 * there is no active workspace and the fallback is the latest promoted one, so the answer there is
 * true, as it is while a workspace page is still loading.
 */
const tcEffectiveWorkspaceIsPromotedAtom = atom<boolean>((get) => {
  const active = get(activeTcWorkspaceAtom);
  return active ? Boolean(active.promoted) : true;
});

export type TcVerificationDecisionKind = 'correct' | 'incorrect';

/**
 * Right column beside Type Curve details (same layout pattern as left page settings).
 * Exclusive: DCA | workspaces list | verification decision | verification tasks
 * | unit-resolution drawer (Story 3).
 */
export type TcDetailsRightPanel =
  | { type: 'dca'; open: boolean }
  | { type: 'workspaces'; open: boolean }
  | { type: 'inventory-workspaces'; open: boolean }
  | { type: 'dca-workspaces'; open: boolean }
  | { type: 'verification'; kind: TcVerificationDecisionKind; open: boolean }
  | { type: 'tasks'; open: boolean }
  | { type: 'unit-resolution'; open: boolean }
  | { type: 'inventory-guide'; open: boolean }
  | { type: 'tc-area'; open: boolean };

const tcDetailsRightPanelAtom = atom<TcDetailsRightPanel>({
  type: 'dca',
  open: false,
});

export interface TcAreaPreview {
  areaId: number;
  geometry: GeoJSON.Geometry | null;
  loading: boolean;
}

const tcAreaPreviewAtom = atom<TcAreaPreview | null>(null);

export type TcWorkspacesListResourceState = {
  loading: boolean;
  data:
    | TypeCurveWorkspacesListResponse
    | TypeCurveWorkspaceResponse[]
    | { error: string }
    | undefined;
};

export type TcWorkspacesListAtom = WritableAtom<
  TcWorkspacesListResourceState,
  [],
  Promise<void>
>;

function useTcWorkspaceDetailResourceAtom(
  workspaceSelfHref: string | null | undefined
) {
  return useMemo(
    () =>
      createResourceAtom<TypeCurveWorkspaceResponse>(
        workspaceSelfHref?.trim() ? workspaceSelfHref : undefined
      ),
    [workspaceSelfHref]
  );
}

function useTcWorkspacesListResourceAtom(
  workspacesCollectionHref: string | null | undefined
) {
  return useMemo(
    () =>
      createResourceAtom<TypeCurveWorkspacesListResponse>(
        workspacesCollectionHref ?? undefined
      ),
    [workspacesCollectionHref]
  );
}

function useTypeCurveHeaderResourceAtom(typeCurveId: string | undefined) {
  return useMemo(
    () =>
      createResourceAtom<TypeCurveHeader>(
        typeCurveId ? `/type_curve_headers/${typeCurveId}` : undefined
      ),
    [typeCurveId]
  );
}

function useTcWorkspaceHeaderResourceAtom(workspaceId: string | undefined) {
  return useMemo(
    () =>
      createResourceAtom<TcWorkspaceHeaderResponse>(
        workspaceId ? `/type_curve_workspaces/${workspaceId}/header` : undefined
      ),
    [workspaceId]
  );
}
// Resolves the "effective" Type Curve header detail for the details page and its
// views. When a workspace is active, every header-detail-scoped read must use THAT
// workspace's cloned header detail; otherwise it falls back to the promoted header
// (fetched by route id). Centralizes the active-vs-promoted choice so call sites
// don't repeat it. Returns `promoted` (page-level context) + `data` (effective),
// plus a combined loading/error and `refresh` for the promoted resource.
function useEffectiveTypeCurveHeader(id: string | undefined) {
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);

  const promotedAtom = useTypeCurveHeaderResourceAtom(id);
  const [promotedResource, refresh] = useAtom(promotedAtom);

  const workspaceAtom = useTcWorkspaceHeaderResourceAtom(
    activeWorkspace ? String(activeWorkspace.id) : undefined
  );
  const workspaceResource = useAtomValue(workspaceAtom);

  const promoted = formatResource(promotedResource);
  const workspace = formatResource(workspaceResource);

  return {
    // Promoted header — page-level identity/context (title, workspaces href, promote…).
    promoted: promoted.data,
    // Effective header — active workspace's when in a workspace, else promoted.
    data: activeWorkspace ? workspace.data : promoted.data,
    // Only count `promoted.loading` when a promoted id was actually requested.
    // A resource created with an undefined href (e.g. the map inside a
    // workspace, which passes id=undefined) stays `loading: true` forever, so
    // including it unconditionally would wedge the map in a loading state.
    loading:
      (id ? promoted.loading : false) ||
      Boolean(activeWorkspace && workspace.loading),
    // Surface the error from whichever source backs `data`. On the workspace
    // page the promoted resource is fetched with the workspace id (not a
    // tc_public_id) and always 404s; keying off `promoted.error` there would
    // wrongly fail consumers (e.g. the map) whose effective data loaded fine.
    error: activeWorkspace ? workspace.error : promoted.error,
    refresh,
  };
}

const createTopLevelTcWorkspaceAtom = createMutationAtom<
  TypeCurveWorkspaceResponse,
  'createTypeCurveWorkspace'
>();

export {
  activeTcWorkspaceAtom,
  createInventoryWorkspaceAtom,
  createTypeCurveWorkspaceAtom,
  createTopLevelTcWorkspaceAtom,
  latestPromotedWorkspaceIdAtom,
  promoteTypeCurveWorkspaceAtom,
  tcAreaPreviewAtom,
  tcDetailsRightPanelAtom,
  tcEffectiveWorkspaceIdAtom,
  tcEffectiveWorkspaceIsPromotedAtom,
  useEffectiveTypeCurveHeader,
  useTcWorkspaceDetailResourceAtom,
  useTcWorkspaceHeaderResourceAtom,
  useTcWorkspacesListResourceAtom,
  useTypeCurveHeaderResourceAtom,
};
